/**
 * 데이터 주도 튜토리얼 — 데이터 타입 (Phaser/외부 의존 0).
 *
 * 한 스텝(step)이 "어떤 영역을 강조하고, 어떤 문구를, 어떻게 넘길지"를 선언한다.
 * 영역은 추상 id(string)로만 지정하고, 실제 화면 좌표 해석은 `TutorialTargetResolver`(앱 어댑터)가 담당한다
 * — 패키지가 특정 게임의 레이아웃/씬 그래프를 모르게 하기 위함(DIP).
 */

/** 스텝 진행 방식. */
export type TutorialAdvanceMode =
  /** 화면 아무 곳이나 탭하면 다음 (설명형). 오버레이가 입력을 차단한다. */
  | "tap"
  /** 강조(컷아웃) 영역 안을 탭해야 다음. 그 외 탭은 무시. 오버레이가 입력을 차단한다. */
  | "spotlightTap"
  /** 실제 액션 완료 시 다음(`TutorialRunner.notifyAction`). 오버레이가 컷아웃 영역 입력을 차단하지 않는다. */
  | "action";

/** 메시지 박스 세로 위치. */
export type TutorialMessagePosition = "top" | "middle" | "bottom";

/** 포인터(손가락) 연출 종류. */
export type TutorialGesture =
  /** 연출 없음. */
  | "none"
  /** 단일 타겟(gestureFrom) 위에서 제자리 탭 펄스 반복. */
  | "tap"
  /** gestureFrom 중심 → gestureTo 중심으로 손가락이 이동하는 드래그 반복. */
  | "drag";

/**
 * 타겟 Rect 보정값(px). 리졸버가 돌려준 Rect에 더해 하이라이트/컷아웃 위치·크기를 미세 조정한다.
 * 좌표·크기 산식을 코드로 바꾸지 않고 데이터(tsv)로 영역을 nudge하기 위함.
 */
export interface TutorialRectOffset {
  readonly dx: number;
  readonly dy: number;
  readonly dw: number;
  readonly dh: number;
}

/**
 * 화면 좌표 사각형 (Phaser 비의존). `TutorialTargetResolver`가 영역 id를 이 값으로 해석한다.
 * 좌상단 원점(x, y) + 크기(width, height).
 */
export interface TutorialRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/** 튜토리얼 한 스텝(데이터). */
export interface TutorialStep {
  /** 시나리오 그룹 키. */
  readonly scenarioId: string;
  /** 1-based 정렬 순서. */
  readonly stepOrder: number;
  /** 스텝 고유 id(분석/디버그용). */
  readonly stepId: string;
  /** 강조할 영역 id 목록(빈 배열 = 타겟 없음 = 전체 dim 메시지 전용). 어댑터가 각각 Rect로 해석. */
  readonly targets: readonly string[];
  /** 메시지 로컬라이즈 키(빈 문자열 = 메시지 없음). */
  readonly messageKey: string;
  /** 진행 방식. */
  readonly advance: TutorialAdvanceMode;
  /** advance="action"일 때 대기할 액션 키(예: "tab.shop"). 그 외 모드에서는 무시. */
  readonly actionKey: string;
  /** 메시지 박스 위치. */
  readonly position: TutorialMessagePosition;
  /** 컷아웃 여백(px). */
  readonly padding: number;
  /** 멀티 타겟을 단일 bounding box로 합칠지. */
  readonly unifyTargets: boolean;
  /** 자동 진행 시간(ms). 0이면 수동(탭/액션). */
  readonly autoAdvanceMs: number;
  /** 타겟 id별 Rect 보정(빈 객체 = 보정 없음). 리졸버 결과에 더해 데이터로 영역을 미세 조정. */
  readonly offsets: Record<string, TutorialRectOffset>;
  /** 포인터(손가락) 연출 종류. */
  readonly gesture: TutorialGesture;
  /** drag 출발 / tap 대상 타겟 id(빈 문자열 = 연출 없음). */
  readonly gestureFrom: string;
  /** drag 도착 타겟 id(tap·none이면 무시). */
  readonly gestureTo: string;
  /**
   * 이 스텝에서 허용할 게임 인터랙션 키 목록(빈 배열 = 무제한 = 게이트 없음).
   * 비어있지 않으면 호출자가 `isInteractionAllowed`로 조회해 목록 밖 인터랙션을 차단한다.
   * (튜토리얼 중 안내받지 않은 버튼/셀 오클릭으로 엉뚱한 팝업이 뜨는 것을 막는 게이트.)
   */
  readonly allowedActions: readonly string[];
}

/** 순서대로 정렬된 한 시나리오의 스텝 목록. */
export type TutorialScenario = readonly TutorialStep[];

/** Rect에 보정값을 더한 새 Rect 반환(원본 불변). offset이 없으면 입력을 그대로 돌려준다. */
export function applyRectOffset(
  rect: TutorialRect,
  offset: TutorialRectOffset | undefined,
): TutorialRect {
  if (!offset) return rect;
  return {
    x: rect.x + offset.dx,
    y: rect.y + offset.dy,
    width: rect.width + offset.dw,
    height: rect.height + offset.dh,
  };
}
